import React, { useState, useEffect, useContext, useMemo } from 'react';
import { FiFileText, FiDownload, FiZap, FiTrendingUp, FiDollarSign, FiPackage, FiPercent, FiClock, FiBarChart2, FiMinusCircle, FiCheckCircle, FiPieChart } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import AuthContext from '../context/AuthContext'; 
import { Doughnut, Line } from 'react-chartjs-2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, Filler } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, Filler);

// Fonction utilitaire pour formater les nombres pour le PDF
const formatNumberForPdf = (num) => {
  return num.toLocaleString('fr-FR').replace(/\s/g, ".");
};

const Reports = () => {
  const { user, openUpgradeModal } = useContext(AuthContext); // Supposons que openUpgradeModal est dans AuthContext
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [compare, setCompare] = useState(false);
  const [compareStartDate, setCompareStartDate] = useState('');
  const [compareEndDate, setCompareEndDate] = useState('');
  const [compareReportData, setCompareReportData] = useState(null);
  const [advancedReports, setAdvancedReports] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const isPremium = user?.plan === 'premium';

  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter === 'today') {
      // Utiliser la date locale pour éviter les décalages UTC
      const now = new Date();
      const offset = now.getTimezoneOffset();
      const todayDate = new Date(now.getTime() - (offset * 60 * 1000));
      const today = todayDate.toISOString().split('T')[0];
      setStartDate(today);
      setEndDate(today);
      handleGenerateReport(today, today); // Génère le rapport pour aujourd'hui
      toast.info("Affichage des rapports pour aujourd'hui.");
    }
  }, [searchParams]);

  const getPercentChange = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const handleGenerateReport = async (start = startDate, end = endDate) => {
    if (!start || !end) {
      toast.error("Veuillez sélectionner une date de début et de fin.");
      return;
    }
    setLoading(true);
    
    if (!compare) {
      setCompareReportData(null);
    }

    try {
      // Récupération parallèle du résumé et des dépenses
      const [summaryResponse, expensesResponse] = await Promise.all([
        api.get('/reports/summary', { params: { startDate: start, endDate: end } }),
        api.get('/expenses', { params: { startDate: start, endDate: end } })
      ]);

      // Filtrage côté client pour s'assurer que les dépenses correspondent à la période
      const createLocalDate = (dateString, isEnd = false) => {
        const [y, m, d] = dateString.split('-').map(Number);
        return new Date(y, m - 1, d, isEnd ? 23 : 0, isEnd ? 59 : 0, isEnd ? 59 : 0, isEnd ? 999 : 0);
      };

      const startObj = createLocalDate(start);
      const endObj = createLocalDate(end, true);

      const filteredExpenses = expensesResponse.data.filter(exp => {
        const expDate = new Date(exp.date);
        return expDate >= startObj && expDate <= endObj;
      });

      const expensesTotal = filteredExpenses.reduce((acc, curr) => acc + curr.amount, 0);
      
      setReportData({
        ...summaryResponse.data,
        expenses: expensesTotal,
        netProfit: summaryResponse.data.profit - expensesTotal
      });

      if (compare && compareStartDate && compareEndDate) {
        const [compareSummary, compareExpenses] = await Promise.all([
          api.get('/reports/summary', { params: { startDate: compareStartDate, endDate: compareEndDate } }),
          api.get('/expenses', { params: { startDate: compareStartDate, endDate: compareEndDate } })
        ]);
        
        const compareStartObj = createLocalDate(compareStartDate);
        const compareEndObj = createLocalDate(compareEndDate, true);

        const filteredCompareExpenses = compareExpenses.data.filter(exp => {
            const expDate = new Date(exp.date);
            return expDate >= compareStartObj && expDate <= compareEndObj;
        });

        const compareExpensesTotal = filteredCompareExpenses.reduce((acc, curr) => acc + curr.amount, 0);
        
        setCompareReportData({
          ...compareSummary.data,
          expenses: compareExpensesTotal,
          netProfit: compareSummary.data.profit - compareExpensesTotal
        });
      } else {
        setCompareReportData(null);
      }

      // Si l'utilisateur est premium, on charge aussi les rapports avancés pour cette période
      if (isPremium) {
        fetchAdvancedReports(start, end);
      }
    } catch (error) {
      toast.error("Impossible de générer le rapport. Votre plan le permet-il ?");
      setReportData(null);
      setCompareReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvancedReports = async (start, end) => {
    try {
      const response = await api.get('/reports/advanced', {
        params: { startDate: start, endDate: end },
      });
      setAdvancedReports(response.data);
    } catch (error) {
      // Ne pas afficher de toast ici pour ne pas être intrusif
      console.error("Impossible de charger les rapports avancés.");
    }
  };

  const handleExportPDF = () => {
    if (!reportData) {
      toast.warn("Veuillez d'abord générer un rapport.");
      return;
    }
    if (!isPremium) {
      toast.info("Exportez vos rapports détaillés en PDF. Passez au plan Premium pour une analyse approfondie.", { icon: "📄" });
      openUpgradeModal();
      return;
    }

    const doc = new jsPDF('p', 'pt', 'a4');
    const totalPagesExp = '{total_pages_count_string}';

    // En-tête du document
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(user?.shopName || 'Rapport d\'activité', 40, 50);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Période du ${new Date(startDate).toLocaleDateString('fr-FR')} au ${new Date(endDate).toLocaleDateString('fr-FR')}`, 40, 70);
    doc.text(`Généré le: ${new Date().toLocaleString('fr-FR')}`, 40, 85);

    // Rapport de base
    autoTable(doc, {
      startY: 110,
      theme: 'grid',
      headStyles: { fillColor: '#2196F3' }, // brand-blue
      head: [['Indicateur', 'Valeur']],
      body: [
        ["Chiffre d'affaires", `${formatNumberForPdf(reportData.revenue)} FCFA`],
        ["Bénéfice Brut", `${formatNumberForPdf(reportData.profit)} FCFA`],
        ["Dépenses", `${formatNumberForPdf(reportData.expenses)} FCFA`],
        ["Bénéfice Net", `${formatNumberForPdf(reportData.netProfit)} FCFA`],
        ["Produits Vendus", reportData.itemsSold],
        ["Marge de bénéfice", reportData.revenue > 0 ? `${((reportData.profit / reportData.revenue) * 100).toFixed(1)} %` : 'N/A'],
      ],
      didDrawPage: function (data) {
        // Pied de page
        let str = 'Page ' + doc.internal.getNumberOfPages();
        if (typeof doc.putTotalPages === 'function') {
          str = str + ' sur ' + totalPagesExp;
        }
        doc.setFontSize(10);
        doc.text(str, data.settings.margin.left, doc.internal.pageSize.height - 30);
        doc.text('Généré par Jaapand-e', doc.internal.pageSize.width - data.settings.margin.right, doc.internal.pageSize.height - 30, { align: 'right' });
      }
    });
    
    // Section Modes de Paiement (Si disponible)
    if (reportData.paymentMethodStats) {
        const paymentRows = Object.entries(reportData.paymentMethodStats).map(([method, amount]) => [method, `${formatNumberForPdf(amount)} FCFA`]);
        if (paymentRows.length > 0) {
            autoTable(doc, { startY: doc.lastAutoTable.finalY + 30, theme: 'grid', headStyles: { fillColor: '#FF9800' }, head: [['Mode de Paiement', 'Montant Encaissé']], body: paymentRows });
        }
    }

    // Rapports avancés pour Premium
    if (isPremium && advancedReports) {
      if (advancedReports.topSoldProducts?.length > 0) {
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 30,
          theme: 'grid',
          headStyles: { fillColor: '#4CAF50' }, // brand-green
          head: [['Top 5 Produits Vendus (par quantité)', 'Quantité']],
          body: advancedReports.topSoldProducts.map(p => [p.name, p.totalQuantity])
        });
      }
      if (advancedReports.topProfitableProducts?.length > 0) {
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 30,
          theme: 'grid',
          headStyles: { fillColor: '#8E44AD' }, // Un violet
          head: [['Top 5 Produits Rentables (par bénéfice)', 'Bénéfice total']],
          body: advancedReports.topProfitableProducts.map(p => [p.name, `${formatNumberForPdf(p.totalProfit)} FCFA`])
        });
      }
      if (advancedReports.topClients?.length > 0) {
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 30,
          theme: 'grid',
          headStyles: { fillColor: '#F39C12' }, // Un orange
          head: [['Top 5 Clients (par total dépensé)', 'Total dépensé']],
          body: advancedReports.topClients.map(c => [c.name, `${formatNumberForPdf(c.totalSpent)} FCFA`])
        });
      }
    }

    // Remplace le placeholder du nombre total de pages
    if (typeof doc.putTotalPages === 'function') {
      doc.putTotalPages(totalPagesExp);
    }

    doc.save(`rapport_jaapande_${startDate}_${endDate}.pdf`);
  };

  const handleExportExcel = () => {
    if (!reportData) {
      toast.warn("Veuillez d'abord générer un rapport.");
      return;
    }
    if (!isPremium) {
      toast.info("Exportez et analysez vos données sur Excel. Passez au plan Premium pour une analyse approfondie.", { icon: "📊" });
      openUpgradeModal();
      return;
    }

    const wb = XLSX.utils.book_new();

    // --- Création de la feuille de calcul unique ---
    let worksheetData = [];

    // En-tête
    worksheetData.push([{ v: user?.shopName || "Rapport d'activité", s: { font: { bold: true, sz: 16 } } }]);
    worksheetData.push([{ v: `Période du ${new Date(startDate).toLocaleDateString('fr-FR')} au ${new Date(endDate).toLocaleDateString('fr-FR')}`, s: { font: { sz: 10 } } }]);
    worksheetData.push([{ v: `Généré le: ${new Date().toLocaleString('fr-FR')}`, s: { font: { sz: 10 } } }]);
    worksheetData.push([]); // Ligne vide

    // Résumé
    worksheetData.push([{ v: "Résumé du rapport", s: { font: { bold: true, sz: 12, color: { rgb: "2196F3" } } } }]);
    const summaryData = [
      { Indicateur: "Chiffre d'affaires (CFA)", Valeur: reportData.revenue },
      { Indicateur: "Bénéfice Brut (CFA)", Valeur: reportData.profit },
      { Indicateur: "Dépenses (CFA)", Valeur: reportData.expenses },
      { Indicateur: "Bénéfice Net (CFA)", Valeur: reportData.netProfit },
      { Indicateur: "Produits Vendus", Valeur: reportData.itemsSold },
      { Indicateur: "Marge de bénéfice (%)", Valeur: reportData.revenue > 0 ? parseFloat(((reportData.profit / reportData.revenue) * 100).toFixed(2)) : 'N/A' },
    ];
    const ws = XLSX.utils.json_to_sheet(worksheetData, { skipHeader: true });
    XLSX.utils.sheet_add_json(ws, summaryData, { origin: -1, skipHeader: false });

    // Modes de Paiement
    if (reportData.paymentMethodStats) {
        XLSX.utils.sheet_add_aoa(ws, [[]], { origin: -1 });
        XLSX.utils.sheet_add_aoa(ws, [[{ v: "Répartition par Mode de Paiement", s: { font: { bold: true, sz: 12, color: { rgb: "FF9800" } } } }]], { origin: -1 });
        const paymentData = Object.entries(reportData.paymentMethodStats).map(([method, amount]) => ({ 'Mode': method, 'Montant (CFA)': amount }));
        XLSX.utils.sheet_add_json(ws, paymentData, { origin: -1, skipHeader: false });
    }

    // Rapports avancés (sur la même feuille)
    if (isPremium && advancedReports) {
      XLSX.utils.sheet_add_aoa(ws, [[]], { origin: -1 }); // Ligne vide

      if (advancedReports.topSoldProducts?.length > 0) {
        XLSX.utils.sheet_add_aoa(ws, [[{ v: "Top 5 Produits Vendus (par quantité)", s: { font: { bold: true, sz: 12, color: { rgb: "4CAF50" } } } }]], { origin: -1 });
        const topSoldData = advancedReports.topSoldProducts.map(p => ({ 'Produit': p.name, 'Quantité vendue': p.totalQuantity }));
        XLSX.utils.sheet_add_json(ws, topSoldData, { origin: -1, skipHeader: false });
        XLSX.utils.sheet_add_aoa(ws, [[]], { origin: -1 });
      }

      if (advancedReports.topProfitableProducts?.length > 0) {
        XLSX.utils.sheet_add_aoa(ws, [[{ v: "Top 5 Produits Rentables (par bénéfice)", s: { font: { bold: true, sz: 12, color: { rgb: "8E44AD" } } } }]], { origin: -1 });
        const topProfitableData = advancedReports.topProfitableProducts.map(p => ({ 'Produit': p.name, 'Bénéfice total (CFA)': p.totalProfit }));
        XLSX.utils.sheet_add_json(ws, topProfitableData, { origin: -1, skipHeader: false });
        XLSX.utils.sheet_add_aoa(ws, [[]], { origin: -1 });
      }

      if (advancedReports.topClients?.length > 0) {
        XLSX.utils.sheet_add_aoa(ws, [[{ v: "Top 5 Clients (par total dépensé)", s: { font: { bold: true, sz: 12, color: { rgb: "F39C12" } } } }]], { origin: -1 });
        const topClientsData = advancedReports.topClients.map(c => ({ 'Client': c.name, 'Total dépensé (CFA)': c.totalSpent }));
        XLSX.utils.sheet_add_json(ws, topClientsData, { origin: -1, skipHeader: false });
      }
    }

    // Ajuster la largeur des colonnes
    const colsWidth = [
      { wch: 35 }, // Colonne A
      { wch: 25 }, // Colonne B
    ];
    ws['!cols'] = colsWidth;

    // Ajouter la feuille au classeur
    XLSX.utils.book_append_sheet(wb, ws, "Rapport");

    XLSX.writeFile(wb, `rapport_jaapande_${startDate}_${endDate}.xlsx`);
  };

  const lineChartData = useMemo(() => {
    if (!advancedReports || !advancedReports.salesOverTime || advancedReports.salesOverTime.length === 0) {
      return null;
    }

    // Robustesse : supporte d.date ou d._id (format MongoDB courant)
    const labels = advancedReports.salesOverTime.map(d => {
        const dateVal = d.date || d._id;
        return new Date(dateVal).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    });

    return {
      labels,
      datasets: [
        {
          label: 'Chiffre d\'affaires',
          data: advancedReports.salesOverTime.map(d => d.revenue || d.totalRevenue || 0),
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 3,
          pointBackgroundColor: '#3B82F6',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.4,
          fill: true,
        },
        {
          label: 'Bénéfice',
          data: advancedReports.salesOverTime.map(d => d.profit || d.totalProfit || 0),
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 3,
          pointBackgroundColor: '#10B981',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.4,
          fill: true,
        },
      ],
    };
  }, [advancedReports]);

  const paymentMethodChartData = useMemo(() => {
    if (!reportData || !reportData.paymentMethodStats) return null;
    
    const labels = Object.keys(reportData.paymentMethodStats);
    const data = Object.values(reportData.paymentMethodStats);
    if (labels.length === 0) return null;
    
    return {
      labels,
      datasets: [{
        data,
        backgroundColor: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#607D8B', '#795548'], // Couleurs pour Espèces, Wave, OM, etc.
        borderWidth: 1,
      }],
    };
  }, [reportData]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold text-gray-800 font-poppins">Rapports</h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <button disabled={!isPremium} onClick={handleExportPDF} className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-xl flex items-center justify-center transition-colors duration-200 disabled:opacity-50">
            <FiDownload className="mr-2" />
            Exporter PDF
          </button>
          <button disabled={!isPremium} onClick={handleExportExcel} className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-xl flex items-center justify-center transition-colors duration-200 disabled:opacity-50">
            <FiDownload className="mr-2" />
            Exporter Excel
          </button>
        </div>
      </div>

      <div className="mb-6 bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
        <h3 className="font-semibold mb-2">Période</h3>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="w-full">
            <label className="block text-xs text-gray-500 mb-1 sm:hidden">Du</label>
            <input 
              type="date" 
              placeholder="Date de début"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-gray-50 border border-gray-200 rounded-xl py-2 px-4 focus:outline-none focus:border-brand-blue w-full" 
            />
          </div>
          <span className="hidden sm:inline text-center">-</span>
          <div className="w-full">
            <label className="block text-xs text-gray-500 mb-1 sm:hidden">Au</label>
            <input 
              type="date" 
              placeholder="Date de fin"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-gray-50 border border-gray-200 rounded-xl py-2 px-4 focus:outline-none focus:border-brand-blue w-full" 
            />
          </div>
          <button onClick={() => handleGenerateReport()} disabled={loading} className="bg-brand-blue hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-xl transition-colors disabled:opacity-50 w-full sm:w-auto mt-2 sm:mt-0">
            Générer
          </button>
        </div>
        {isPremium && (
          <div className="mt-4">
            <label className="flex items-center">
              <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} className="h-4 w-4 text-brand-blue focus:ring-brand-blue border-gray-300 rounded" />
              <span className="ml-2 text-sm text-gray-700">Comparer à une autre période</span>
            </label>
            {compare && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2 pl-6">
                <input type="date" value={compareStartDate} onChange={(e) => setCompareStartDate(e.target.value)} className="bg-gray-50 border border-gray-200 rounded-xl py-2 px-4 focus:outline-none focus:border-brand-blue w-full" />
                <span className="hidden sm:inline text-center">-</span>
                <input type="date" value={compareEndDate} onChange={(e) => setCompareEndDate(e.target.value)} className="bg-gray-50 border border-gray-200 rounded-xl py-2 px-4 focus:outline-none focus:border-brand-blue w-full" />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-blue-50 text-brand-blue">
              <FiTrendingUp className="h-6 w-6" />
            </div>
            {compareReportData && (
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${reportData.revenue >= compareReportData.revenue ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {reportData.revenue >= compareReportData.revenue ? '+' : ''}{getPercentChange(reportData.revenue, compareReportData.revenue).toFixed(1)}%
              </span>
            )}
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Chiffre d'affaires</p>
            <p className="text-2xl font-extrabold text-gray-800">{reportData ? `${reportData.revenue.toLocaleString('fr-FR')} CFA` : '...'}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-green-50 text-brand-green">
              <FiDollarSign className="h-6 w-6" />
            </div>
            {compareReportData && (
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${reportData.profit >= compareReportData.profit ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {reportData.profit >= compareReportData.profit ? '+' : ''}{getPercentChange(reportData.profit, compareReportData.profit).toFixed(1)}%
              </span>
            )}
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Bénéfice Brut</p>
            <p className="text-2xl font-extrabold text-gray-800">{reportData ? `${reportData.profit.toLocaleString('fr-FR')} CFA` : '...'}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-red-50 text-red-500">
              <FiMinusCircle className="h-6 w-6" />
            </div>
            {compareReportData && (
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${reportData.expenses <= compareReportData.expenses ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {reportData.expenses > compareReportData.expenses ? '+' : ''}{getPercentChange(reportData.expenses, compareReportData.expenses).toFixed(1)}%
              </span>
            )}
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Total Dépenses</p>
            <p className="text-2xl font-extrabold text-gray-800">{reportData ? `${reportData.expenses.toLocaleString('fr-FR')} CFA` : '...'}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600">
              <FiCheckCircle className="h-6 w-6" />
            </div>
            {compareReportData && (
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${reportData.netProfit >= compareReportData.netProfit ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {reportData.netProfit >= compareReportData.netProfit ? '+' : ''}{getPercentChange(reportData.netProfit, compareReportData.netProfit).toFixed(1)}%
              </span>
            )}
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Bénéfice Net (Réel)</p>
            <p className="text-2xl font-extrabold text-gray-800">{reportData ? `${reportData.netProfit.toLocaleString('fr-FR')} CFA` : '...'}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-purple-50 text-purple-500">
              <FiPackage className="h-6 w-6" />
            </div>
            {compareReportData && (
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${reportData.itemsSold >= compareReportData.itemsSold ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {reportData.itemsSold >= compareReportData.itemsSold ? '+' : ''}{getPercentChange(reportData.itemsSold, compareReportData.itemsSold).toFixed(1)}%
              </span>
            )}
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Produits Vendus</p>
            <p className="text-2xl font-extrabold text-gray-800">{reportData ? reportData.itemsSold : '...'}</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-yellow-50 text-yellow-500">
              <FiPercent className="h-6 w-6" />
            </div>
            {compareReportData && (() => {
              const currMargin = reportData.revenue > 0 ? (reportData.profit / reportData.revenue) * 100 : 0;
              const prevMargin = compareReportData.revenue > 0 ? (compareReportData.profit / compareReportData.revenue) * 100 : 0;
              const diff = currMargin - prevMargin;
              return (
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${diff >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {diff >= 0 ? '+' : ''}{diff.toFixed(1)} pts
                </span>
              );
            })()}
          </div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Marge de Bénéfice</p>
            <p className="text-2xl font-extrabold text-gray-800">{reportData && reportData.revenue > 0 ? `${((reportData.profit / reportData.revenue) * 100).toFixed(1)} %` : '0 %'}</p>
          </div>
        </div>
      </div>

      {/* Section Répartition des Paiements (Caisse vs Mobile Money) */}
      {paymentMethodChartData && reportData?.paymentMethodStats && (
        <div className="mt-6 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 flex items-center"><FiPieChart className="mr-2"/>Répartition des Encaissements</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="h-64 flex items-center justify-center">
                    <Doughnut data={paymentMethodChartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' } } }} />
                </div>
                <div className="space-y-3">
                    {Object.entries(reportData.paymentMethodStats).map(([method, amount]) => (
                        <div key={method} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                            <span className="font-medium text-gray-700">{method}</span>
                            <span className="font-bold text-gray-900">{amount.toLocaleString('fr-FR')} CFA</span>
                        </div>
                    ))}
                    <div className="pt-3 border-t border-gray-200 flex justify-between items-center">
                        <span className="font-bold text-gray-800">Total Encaissé</span>
                        <span className="font-bold text-brand-blue text-lg">{reportData.revenue.toLocaleString('fr-FR')} CFA</span>
                    </div>
                </div>
            </div>
        </div>
      )}

      {isPremium && advancedReports?.stockForecasts?.length > 0 && (
        <div className="mt-8 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-xl font-semibold mb-4 text-gray-700">Prévisions de Stock</h3>
          <p className="text-sm text-gray-500 mb-6">Basé sur les ventes de la période sélectionnée, voici une estimation du moment où vous devriez réapprovisionner certains produits populaires.</p>
          <ul className="space-y-4">
            {advancedReports.stockForecasts.map(forecast => (
              <li key={forecast.productId} className="flex flex-col sm:flex-row justify-between sm:items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="mb-2 sm:mb-0">
                  <p className="font-semibold text-gray-800">{forecast.productName}</p>
                  <p className="text-xs text-gray-600">Vitesse de vente : ~{forecast.salesVelocity.toFixed(1)} unités/jour</p>
                </div>
                <div className="flex items-center text-orange-600 font-semibold">
                  <FiClock className="mr-2" />
                  <span>
                    Rupture de stock estimée dans ~{forecast.daysUntilStockout} jours
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isPremium && advancedReports && (
        <div className="mt-8 space-y-8">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-xl font-semibold mb-4 text-gray-700 flex items-center"><FiBarChart2 className="mr-2"/>Évolution des Ventes et Bénéfices</h3>
            {lineChartData ? (
              <div className="h-80">
                <Line
                  data={lineChartData}
                  options={{ 
                    responsive: true, 
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index',
                      intersect: false,
                    },
                    plugins: { 
                      legend: { 
                        position: 'top',
                        align: 'end',
                        labels: {
                          usePointStyle: true,
                          boxWidth: 8,
                          padding: 20,
                          font: { family: "'Poppins', sans-serif", size: 12 }
                        }
                      }, 
                      title: { display: false },
                      tooltip: {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        titleColor: '#111827',
                        bodyColor: '#4B5563',
                        borderColor: '#E5E7EB',
                        borderWidth: 1,
                        padding: 12,
                        boxPadding: 6,
                        usePointStyle: true,
                        titleFont: { size: 13, weight: 'bold', family: "'Poppins', sans-serif" },
                        bodyFont: { size: 12, family: "'Poppins', sans-serif" },
                        callbacks: {
                          label: function(context) {
                            let label = context.dataset.label || '';
                            if (label) {
                                label += ': ';
                            }
                            if (context.parsed.y !== null) {
                                label += context.parsed.y.toLocaleString('fr-FR') + ' CFA';
                            }
                            return label;
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        grid: { display: false, drawBorder: false },
                        ticks: { font: { size: 11 } }
                      },
                      y: {
                        grid: { color: '#F3F4F6', borderDash: [5, 5], drawBorder: false },
                        ticks: {
                          font: { size: 11 },
                          callback: function(value) {
                            if (value >= 1000000) return (value/1000000).toFixed(1) + 'M';
                            if (value >= 1000) return (value/1000).toFixed(0) + 'k';
                            return value;
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p>Aucune donnée d'évolution disponible pour la période sélectionnée.</p>
                <p className="text-xs mt-1">Assurez-vous d'avoir enregistré des ventes pendant ces dates.</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-semibold mb-4">Top 5 Produits les plus Rentables</h3>
              {advancedReports.topProfitableProducts?.length > 0 ? (
                <div className="h-64 flex items-center justify-center">
                  <Doughnut
                    data={{
                      labels: advancedReports.topProfitableProducts.map(p => p.name),
                      datasets: [{
                        label: 'Bénéfice (CFA)',
                        data: advancedReports.topProfitableProducts.map(p => p.totalProfit),
                        backgroundColor: ['#4CAF50', '#81C784', '#A5D6A7', '#C8E6C9', '#E8F5E9'],
                        borderColor: '#fff',
                        borderWidth: 2,
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { position: 'right' } }
                    }}
                  />
                </div>
              ) : <p className="text-center text-gray-500">Données insuffisantes.</p>
              }
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-semibold mb-4">Top 5 Produits les plus Vendus</h3>
              {advancedReports.topSoldProducts?.length > 0 ? (
                <div className="h-64 flex items-center justify-center">
                  <Doughnut
                    data={{
                      labels: advancedReports.topSoldProducts.map(p => p.name),
                      datasets: [{
                        label: 'Quantité vendue',
                        data: advancedReports.topSoldProducts.map(p => p.totalQuantity),
                        backgroundColor: ['#2196F3', '#64B5F6', '#90CAF9', '#BBDEFB', '#E3F2FD'],
                        borderColor: '#fff',
                        borderWidth: 2,
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { position: 'right' } }
                    }}
                  />
                </div>
              ) : <p className="text-center text-gray-500">Données insuffisantes.</p>
              }
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-semibold mb-4">Classement des Clients</h3>
              <ul className="space-y-4">
                {advancedReports.topClients?.length > 0 ? advancedReports.topClients.map((c, index) => (
                  <li key={c._id} className="flex items-center">
                    <span className="text-lg font-bold text-gray-400 w-8">{index + 1}.</span>
                    <span className="font-semibold text-gray-800 flex-grow">{c.name}</span>
                    <span className="font-bold text-brand-blue">{c.totalSpent.toLocaleString('fr-FR')} CFA</span>
                  </li>
                )) : <p className="text-center text-gray-500">Données insuffisantes.</p>}
              </ul>
            </div>
        </div>
      )}

      {!isPremium && (
        <div className="mt-8 text-center bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-xl font-bold text-gray-800">Passez au plan Premium</h3>
          <p className="text-gray-600 mt-2 mb-4">Accédez à des graphiques, des analyses détaillées par produit et client, et exportez vos données en PDF ou Excel.</p>
          {/* On réutilise le bouton qui ouvre le modal d'upgrade */}
          <button onClick={() => openUpgradeModal()} className="bg-brand-green text-white font-bold py-2 px-6 rounded-xl flex items-center justify-center mx-auto">
            <FiZap className="mr-2" />
            Mettre à niveau
          </button>
        </div>
      )}
    </div>
  );
};

export default Reports;